// «Crea con AI»: lo studio scrive i titoli (uno per riga), l'AI locale scrive le schede una alla volta
// (con ricerca web facoltativa: sul web va solo il titolo del servizio) e le salva IN BOZZA nel catalogo.
// Lo studio poi le rilegge, corregge e pubblica.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Sparkles, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio`;
const MAX_TITOLI = 15;

export async function chiediAi(corpo) {
  try {
    return (await axios.post(`${API}/catalogo/ai-proposta`, corpo, { timeout: 300000 })).data;
  } catch (err) {
    throw new Error(err.response?.data?.detail || "L'AI non ha risposto: riprova tra poco");
  }
}

export default function CreaConAi({ aperta, onChiudi, servizi, servizio, voci, onCreate }) {
  const [srv, setSrv] = useState(servizio);
  const [categoria, setCategoria] = useState('');
  const [titoli, setTitoli] = useState('');
  const [istruzioni, setIstruzioni] = useState('');
  const [web, setWeb] = useState(true);
  const [stato, setStato] = useState([]);       // [{titolo, esito: 'attesa'|'lavoro'|'ok'|'errore', msg}]
  const [lavoro, setLavoro] = useState(false);
  const elenco = titoli.split('\n').map((t) => t.trim()).filter((t) => t.length >= 3).slice(0, MAX_TITOLI);
  const categorie = [...new Set(voci.filter((v) => v.servizio === srv).map((v) => v.categoria).filter(Boolean))];

  React.useEffect(() => { if (aperta && !lavoro) { setSrv(servizio); setStato([]); } }, [aperta]); // eslint-disable-line react-hooks/exhaustive-deps

  const avvia = async () => {
    setLavoro(true);
    const righe = elenco.map((t) => ({ titolo: t, esito: 'attesa', msg: '' }));
    setStato(righe);
    const aggiorna = (i, campi) => setStato((s) => s.map((r, j) => (j === i ? { ...r, ...campi } : r)));
    let ordine = Math.max(0, ...voci.filter((v) => v.servizio === srv).map((v) => v.ordine || 0));
    let fatte = 0;
    for (let i = 0; i < righe.length; i += 1) {
      aggiorna(i, { esito: 'lavoro' });
      try {
        const p = await chiediAi({ servizio: srv, categoria, titolo: righe[i].titolo, istruzioni, web });
        ordine += 10;
        await axios.post(`${API}/catalogo`, { servizio: srv, categoria, titolo: righe[i].titolo, sommario: p.sommario,
          descrizione: p.descrizione, documenti: p.documenti, tempi: p.tempi, costo: p.costo, ordine, pubblicata: false,
          prenotazione: '', fonti_ai: p.fonti.map((f) => f.url) });
        fatte += 1;
        aggiorna(i, { esito: 'ok', msg: p.avviso || `${p.documenti.length} documenti · ${p.fonti.length} fonti` });
      } catch (err) {
        aggiorna(i, { esito: 'errore', msg: err.response?.data?.detail || err.message });
      }
    }
    setLavoro(false);
    if (fatte) { toast.success(`${fatte} schede create in bozza: rileggile e pubblicale`); onCreate(); }
  };

  return (
    <Dialog open={aperta} onOpenChange={(o) => { if (!o && !lavoro) onChiudi(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> Crea schede con AI</DialogTitle></DialogHeader>
        <div className="space-y-3 min-w-0">
          <p className="text-sm text-slate-600">Scrivi i titoli delle pratiche: l'AI dello studio (locale) scrive per ognuna spiegazione e documenti,
            nello stile delle schede già pubblicate. Le schede nascono <b>in bozza</b>: rileggile prima di pubblicarle.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Servizio</Label>
              <select className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-white" value={srv} disabled={lavoro} onChange={(e) => setSrv(e.target.value)}>
                {servizi.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Categoria</Label>
              <Input list="categorie-ai" value={categoria} maxLength={80} disabled={lavoro} placeholder="es. Pratiche" onChange={(e) => setCategoria(e.target.value)} />
              <datalist id="categorie-ai">{categorie.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
          </div>
          <div>
            <Label>Titoli (uno per riga, massimo {MAX_TITOLI})</Label>
            <Textarea rows={5} value={titoli} disabled={lavoro} onChange={(e) => setTitoli(e.target.value)}
              placeholder={'Disdetta contratto acqua\nVoltura TARI\nRimborso TARI pagata due volte'} />
          </div>
          <div>
            <Label>Istruzioni per l'AI (facoltative)</Label>
            <Input value={istruzioni} maxLength={1500} disabled={lavoro} onChange={(e) => setIstruzioni(e.target.value)}
              placeholder="es. «testi brevi», «scrivi che il preventivo è gratuito»" />
          </div>
          <label className="flex items-center gap-2 text-sm" title="Sul web va solo il titolo del servizio, mai dati dei clienti">
            <input type="checkbox" checked={web} disabled={lavoro} onChange={(e) => setWeb(e.target.checked)} />
            Cerca sul web le regole aggiornate (consigliato per leggi, bonus, agevolazioni)
          </label>
          {stato.length > 0 && (
            <div className="rounded-lg border divide-y">
              {stato.map((r) => (
                <div key={r.titolo} className="flex items-start gap-2 p-2 text-sm">
                  {r.esito === 'lavoro' ? <Loader2 className="w-4 h-4 mt-0.5 animate-spin text-violet-600" />
                    : r.esito === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600" />
                      : r.esito === 'errore' ? <XCircle className="w-4 h-4 mt-0.5 text-red-600" /> : <span className="w-4 h-4 mt-0.5 rounded-full border" />}
                  <div className="min-w-0"><div className="font-medium">{r.titolo}</div>{r.msg && <div className="text-xs text-slate-500 break-words">{r.msg}</div>}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={lavoro} onClick={onChiudi}>Chiudi</Button>
          <Button onClick={avvia} disabled={lavoro || !elenco.length} className="bg-violet-600 hover:bg-violet-700">
            {lavoro ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scrivo… (circa 30-60 secondi a scheda)</>
              : <><Sparkles className="w-4 h-4 mr-2" /> Crea {elenco.length || ''} {elenco.length === 1 ? 'scheda' : 'schede'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
